// IP geolocation helper (Phase 12.15) — delegates to the back-end
// /api/ipgeo endpoint instead of calling ip2location.io directly
// from the browser.
//
// Why server-side now:
//   1. CSP connect-src 'self' actually forbids the old path (it was
//      silently violating the policy that this very page declares).
//   2. The free ip2location.io tier is rate-limited; a busy session
//      with N admins would hit 429.
//   3. The same IP gets resolved by every user on every Overview
//      open — pure waste. The back-end has a per-IP cache in
//      timestop.ip_geo, so steady-state cost is one PDO lookup.
//
// The endpoint returns {ip, loc, isp, country, city, region, lat, lng,
// source, fetched_at}. `source` is one of "cache", "fresh", "stale",
// "skip". We treat "skip" (private/loopback IP) the same as a
// successful empty result — just show the IP and "private/local".
//
// Phase 12.21: the IP/Location card on the Overview tab is now
// collapsible. Collapsed = single line "IP — city, country". Expanded
// = the same plus a two-column detail panel (IP, country, city,
// region, ISP, lat/lng) and a small map preview rendered from
// /api/staticmap (server-side OSM tile proxy — see admin/api/index.php
// case 'staticmap'). The card click toggles open/close; the toggle
// state is per-modal (re-closes on closeModal) and survives tab
// switches so a user can flip to Logs/Commands and back without
// losing their expanded view.

import type { TimestopState } from './state';
import type { ApiFetchConfig } from './api/client';
import { apiFetch } from './api/client';
import { t } from './i18n';

export interface IpGeoInfo {
  ip: string;
  loc: string;
  isp: string;
  country: string;
  city: string;
  region: string;
  lat: number | null;
  lng: number | null;
  source: string;
  fetched_at: string | null;
}

// Module-scope last successful result, so expandIpDetails() can
// re-render the details panel without a second network round-trip.
// Cleared by closeModal via window.__clearIpGeoCache (main.ts wires
// the modal close path). Cleared on every fresh resolve so a
// different terminal doesn't accidentally show the previous
// terminal's details.
let lastGeo: IpGeoInfo | null = null;
let lastIp: string | null = null;

export function clearIpGeoCache(): void {
  lastGeo = null;
  lastIp = null;
  // Reset the IP map's pan/zoom state so the next terminal opens
  // at the resolved centre + zoom 14, not wherever the previous
  // terminal was panned/zoomed to.
  mapState.centreLat = null;
  mapState.centreLng = null;
  mapState.zoom = 14;
}

export function getLastIpGeo(): IpGeoInfo | null {
  return lastGeo;
}

// Resolve the IP the terminal reported, ask /api/ipgeo, and paint
// the Overview card. Updates #ov-ip in place. The card already
// starts as "resolving..."; renderTabOverview() set that before
// calling us.
//
// `config` is the {apiBase, csrfToken} the rest of the bundle uses
// for apiFetch() — we accept it as a parameter (not via window.*)
// so this helper stays testable and matches the rest of the
// src/ tree.
export async function resolveAndShowIp(
  state: TimestopState,
  config: ApiFetchConfig,
): Promise<void> {
  const el = document.getElementById('ov-ip');
  if (!el) return;
  const ct = state.currentTerminal;
  const ip: string | null =
    ct && (ct.ip || ct.clientip || ct.client_ip) ? String(ct.ip || ct.clientip || ct.client_ip) : null;

  if (!ip) {
    el.textContent = t('common.unavailable');
    // Hide the expand affordance when there's no IP to look up.
    const exp = document.getElementById('ov-ip-expand');
    if (exp) exp.classList.add('hidden');
    lastGeo = null;
    lastIp = null;
    return;
  }
  el.textContent = ip + ' (' + t('tab_overview.resolving') + ')';

  const r = await apiFetch('ipgeo?ip=' + encodeURIComponent(ip), { method: 'GET' }, config);
  if (!el.isConnected) return; // modal closed while we waited

  if (!r.ok || !r.data || typeof r.data !== 'object') {
    el.textContent = ip + ' — ' + t('tab_overview.geo_lookup_failed_short');
    const exp = document.getElementById('ov-ip-expand');
    if (exp) exp.classList.remove('hidden');
    return;
  }
  const data = r.data as Partial<IpGeoInfo> & { error?: string };
  if (data.error) {
    el.textContent = ip + ' — ' + String(data.error);
    const exp = document.getElementById('ov-ip-expand');
    if (exp) exp.classList.remove('hidden');
    return;
  }
  const loc = data.loc && data.loc !== '—' ? data.loc : '';
  // The collapsed line shows IP + city/region/country only. ISP is
  // intentionally hidden here so the one-line summary doesn't get
  // noisy — it's surfaced in the expanded details panel where
  // there's room for it.
  const line1 = ip;
  const line2 = loc;
  el.textContent = line2 ? line1 + ' · ' + line2 : line1;
  el.style.whiteSpace = '';
  // Note: we intentionally do NOT surface `source` to the user. The
  // dev console shows it; the modal stays clean. If we ever need
  // a "stale" badge, that's a one-line add here.
  if (data.source) {
    try { console.debug('[timestop] ipgeo', ip, data.source, data.fetched_at); } catch { /* ignore */ }
  }
  // Cache for the expand panel. Stored at module scope so the
  // detail panel can re-render without a second fetch.
  const geo: IpGeoInfo = {
    ip: String(data.ip ?? ip),
    loc: String(data.loc ?? ''),
    isp: String(data.isp ?? ''),
    country: String(data.country ?? ''),
    city: String(data.city ?? ''),
    region: String(data.region ?? ''),
    lat: typeof data.lat === 'number' ? data.lat : null,
    lng: typeof data.lng === 'number' ? data.lng : null,
    source: String(data.source ?? ''),
    fetched_at: data.fetched_at ? String(data.fetched_at) : null,
  };
  lastGeo = geo;
  lastIp = ip;
  // Show the expand affordance only when we have something to
  // expand into (real coords, or any non-empty city/region/isp).
  const exp = document.getElementById('ov-ip-expand');
  if (exp) {
    const hasCoords = geo.lat != null && geo.lng != null;
    const hasDetails = geo.city || geo.region || geo.country || geo.isp;
    if (hasCoords || hasDetails) exp.classList.remove('hidden');
    else exp.classList.add('hidden');
  }
  // If the panel was already open (user expanded before resolve
  // returned), refresh it with the fresh data.
  const panel = document.getElementById('ov-ip-details');
  if (panel && !panel.classList.contains('hidden')) {
    paintIpDetails(panel, geo);
  }
}

// expandIpDetails -- toggle the IP/Location details panel. Reads
// the cached geo from the last resolveAndShowIp call. If nothing
// has been resolved yet (no IP / still resolving / no terminal),
// does nothing. The toggle is a CSS class flip on a single
// container; we re-paint on every open so a fresh resolve (or a
// fresh openTerminal on a different terminal) is reflected without
// a second network round-trip.
export function expandIpDetails(): void {
  const wrap = document.getElementById('ov-ip-card');
  if (!wrap) return;
  const panel = document.getElementById('ov-ip-details');
  if (!panel) return;
  const willOpen = panel.classList.contains('hidden');
  panel.classList.toggle('hidden');
  wrap.classList.toggle('expanded', willOpen);
  // Update the chevron affordance.
  const chev = document.getElementById('ov-ip-chevron');
  if (chev) {
    chev.textContent = willOpen ? '▾' : '▸';
  }
  if (willOpen) {
    // Re-paint with whatever's cached. If the cache is empty
    // (shouldn't happen — the click affordance is hidden until
    // we have a result), bail with a friendly placeholder.
    if (!lastGeo) {
      panel.innerHTML = '<div class="text-gray-400 py-2">' + t('tab_overview.no_geo_data') + '</div>';
      return;
    }
    paintIpDetails(panel, lastGeo);
  }
}

function paintIpDetails(panel: HTMLElement, geo: IpGeoInfo): void {
  const escape = (s: string): string => String(s || '')
    .replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] ?? c));
  const row = (label: string, value: string): string =>
    '<div class="flex justify-between gap-2 py-1 border-b border-gray-200 last:border-0">'
    + '<span class="text-gray-500">' + escape(label) + '</span>'
    + '<span class="font-mono text-gray-800 text-right break-all">' + escape(value || '—') + '</span>'
    + '</div>';
  const hasCoords = geo.lat != null && geo.lng != null;
  // The current pan/zoom state is the resolved centre + zoom 14
  // for fresh paints. Pan/zoom operations update these via the
  // module-scope mapState (see below) and re-paint the tile.
  // We pull lat/lng out as non-null locals here so TS carries
  // the `hasCoords` narrowing through the rest of the function
  // (the hasCoords ? : ternary below can otherwise get confused
  // once we touch module-scope variables).
  const lat: number = (geo.lat != null) ? geo.lat : 0;
  const lng: number = (geo.lng != null) ? geo.lng : 0;
  const zoom = (typeof mapState.zoom === 'number') ? mapState.zoom : 14;
  mapState.centreLat = lat;
  mapState.centreLng = lng;
  mapState.zoom = zoom;

  // ---- map widget ----
  // The BO doesn't ship a JS map library and CSP blocks loading one
  // from a CDN, so this is a small custom widget built on top of
  // /api/staticmap (server-side OSM tile proxy). The tile is a
  // single 256x256 PNG; the user pans by dragging (pointer
  // events) and zooms with the +/- buttons. Every change
  // recomputes the new (lat, lng, zoom), hits the proxy, and
  // swaps the <img> src. The marker is a CSS-positioned dot
  // anchored to the centre pixel of the current view.
  //
  // One tile has ~150m accuracy at zoom 14 in a city, so the
  // pan step is "half a tile" — that lands the user on a fresh
  // tile boundary on the next render and keeps the visible
  // experience smooth. Smaller steps would be more responsive
  // but would request several tiles before the user actually
  // moved a full tile-width, which is wasteful.
  const mapHtml = hasCoords
    ? '<div class="ov-ip-map relative rounded-2xl overflow-hidden border border-gray-200 bg-gray-100 select-none" '
      + 'style="width:256px;height:256px;max-width:100%;touch-action:none;cursor:grab;" '
      + 'data-lat="' + lat + '" data-lng="' + lng + '" data-zoom="' + zoom + '">'
      // Tile image. data-* attributes on the wrapper carry the
      // current view state; the script reads them after each
      // fetch to know whether the response still matches.
      + '<img class="ov-ip-map-tile block w-full h-full object-cover" '
      + 'draggable="false" '
      + 'src="/api/staticmap?lat=' + lat + '&amp;lng=' + lng + '&amp;zoom=' + zoom + '" '
      + 'alt="' + escape(t('tab_overview.map_alt', { lat: lat.toFixed(4), lng: lng.toFixed(4) })) + '" '
      + 'width="256" height="256" loading="lazy">'
      // Marker dot — anchored to the centre pixel.
      + '<span class="ov-ip-map-marker pointer-events-none absolute" '
      + 'style="left:50%;top:50%;transform:translate(-50%,-50%);'
      + 'width:18px;height:18px;border-radius:50%;'
      + 'background:#ef4444;border:3px solid #fff;'
      + 'box-shadow:0 0 0 1px rgba(0,0,0,0.25),0 2px 4px rgba(0,0,0,0.3);">'
      + '</span>'
      // Floating control cluster: + / − zoom buttons + current
      // zoom readout. Positioned top-right so it doesn't fight
      // the marker dot in the centre. Pointer events are
      // re-enabled on the buttons (the parent is drag-aware and
      // swallows drags if they're not stopped).
      + '<div class="ov-ip-map-controls absolute top-2 right-2 flex flex-col gap-1 bg-white/90 rounded-xl shadow border border-gray-200 p-1 text-xs">'
      + '<button type="button" class="ov-ip-map-zoom-in w-7 h-7 rounded-lg hover:bg-gray-100 font-bold leading-none" aria-label="' + escape(t('tab_overview.zoom_in')) + '">+</button>'
      + '<button type="button" class="ov-ip-map-zoom-out w-7 h-7 rounded-lg hover:bg-gray-100 font-bold leading-none" aria-label="' + escape(t('tab_overview.zoom_out')) + '">−</button>'
      + '<span class="ov-ip-map-zoom-readout text-center text-gray-600 font-mono text-[10px] py-0.5">z' + zoom + '</span>'
      + '<button type="button" class="ov-ip-map-reset w-7 h-7 rounded-lg hover:bg-gray-100 text-[10px] leading-none" aria-label="' + escape(t('tab_overview.recentre')) + '" title="' + escape(t('tab_overview.recentre_title')) + '">◎</button>'
      + '</div>'
      // Loading + error overlays
      + '<div class="ov-ip-map-loading hidden absolute inset-0 bg-white/60 items-center justify-center text-gray-500 text-xs">' + escape(t('tab_overview.map_loading')) + '</div>'
      + '<div class="ov-ip-map-error hidden absolute inset-0 flex items-center justify-center bg-white/80 text-red-500 text-xs text-center p-4">' + escape(t('tab_overview.map_error')) + '</div>'
      + '</div>'
    : '<div class="text-gray-400 py-2 text-xs">' + escape(t('tab_overview.no_coords')) + '</div>';

  // Side-by-side layout: map on the left, details column on the
  // right. Stacks vertically on narrow viewports (sm: breakpoint)
  // because the details column has 6 rows of monospace text and
  // doesn't compress well below ~280px.
  panel.innerHTML = hasCoords
    ? '<div class="grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-4">'
        + '<div class="ov-ip-map-col">' + mapHtml + '</div>'
        + '<div class="ov-ip-details-col">'
          + '<div class="text-sm">' + row(t('tab_overview.ip_label'), geo.ip) + row(t('tab_overview.country_label'), geo.country) + row(t('tab_overview.city_label'), geo.city) + row(t('tab_overview.region_label'), geo.region) + row(t('tab_overview.isp_label'), geo.isp) + row(t('tab_overview.coords_label'), lat.toFixed(4) + ', ' + lng.toFixed(4)) + '</div>'
          + '<div class="text-[10px] text-gray-400 mt-2">' + escape(t('tab_overview.map_attribution')) + '</div>'
        + '</div>'
      + '</div>'
    : '<div class="text-sm">' + row(t('tab_overview.ip_label'), geo.ip) + row(t('tab_overview.country_label'), geo.country) + row(t('tab_overview.city_label'), geo.city) + row(t('tab_overview.region_label'), geo.region) + row(t('tab_overview.isp_label'), geo.isp) + row(t('tab_overview.coords_label'), '') + '</div>'
      + '<div class="text-[10px] text-gray-400 mt-2">' + escape(t('tab_overview.map_attribution')) + '</div>';

  if (hasCoords) {
    // Wire the pan/zoom controls. Done after innerHTML so the
    // elements actually exist in the DOM.
    wireMapControls(panel);
  }
}

// Module-scope pan/zoom state for the IP map. Survives tab
// switches (the panel's innerHTML is rebuilt by renderTabOverview
// only if the panel is hidden; when it's already open, the
// existing DOM is preserved per Phase 10.2b's dirty-tab rule —
// but we rebuild the innerHTML on every open from paintIpDetails
// anyway so this state is re-seeded each open to the resolved
// centre + zoom 14). The state is per-modal: clearIpGeoCache
// zeros it out on closeModal.
interface MapState {
  centreLat: number | null;
  centreLng: number | null;
  zoom: number;
}
const mapState: MapState = { centreLat: null, centreLng: null, zoom: 14 };

// Slippy map: convert a (lat, lng) + a pixel offset within the
// 256x256 tile to a new (lat, lng) at the same zoom. Used to
// translate a drag delta into a new centre.
function tileToLatLng(lat: number, lng: number, zoom: number, dpxX: number, dpxY: number): { lat: number; lng: number } {
  const n = Math.pow(2, zoom);
  // Current pixel coords at this zoom
  const xPx = ((lng + 180.0) / 360.0) * 256.0 * n;
  const latRad = (lat * Math.PI) / 180.0;
  const yPx = (1.0 - Math.log(Math.tan(latRad) + 1.0 / Math.cos(latRad)) / Math.PI) / 2.0 * 256.0 * n;
  // Shift by the drag delta
  const xPx2 = xPx - dpxX;
  const yPx2 = yPx - dpxY;
  const lng2 = (xPx2 / (256.0 * n)) * 360.0 - 180.0;
  const lat2 = (Math.atan(Math.sinh(Math.PI * (1.0 - 2.0 * yPx2 / (256.0 * n)))) * 180.0) / Math.PI;
  return { lat: lat2, lng: lng2 };
}

// Wire the pan/zoom control cluster + drag-to-pan on the map
// element. Re-queries the DOM each call because paintIpDetails
// rewrites innerHTML between toggles.
function wireMapControls(panel: HTMLElement): void {
  const mapEl = panel.querySelector('.ov-ip-map') as HTMLElement | null;
  if (!mapEl) return;
  const tile = mapEl.querySelector('.ov-ip-map-tile') as HTMLImageElement | null;
  const readout = mapEl.querySelector('.ov-ip-map-zoom-readout') as HTMLElement | null;
  const loading = mapEl.querySelector('.ov-ip-map-loading') as HTMLElement | null;
  const errorBox = mapEl.querySelector('.ov-ip-map-error') as HTMLElement | null;
  const zoomIn = mapEl.querySelector('.ov-ip-map-zoom-in') as HTMLButtonElement | null;
  const zoomOut = mapEl.querySelector('.ov-ip-map-zoom-out') as HTMLButtonElement | null;
  const reset = mapEl.querySelector('.ov-ip-map-reset') as HTMLButtonElement | null;
  if (!tile) return;

  // We need access to the original resolved centre for the reset
  // button. Stash it as a closure variable; updated each paint.
  // (lastGeo is the same object, but the snapshot lets us
  // restore the exact lat/lng even if the resolved centre
  // changes during this session — e.g. user opens a different
  // terminal, expands, then comes back.)
  const originalLat = (typeof mapState.centreLat === 'number') ? mapState.centreLat : null;
  const originalLng = (typeof mapState.centreLng === 'number') ? mapState.centreLng : null;
  let currentZoom: number = (typeof mapState.zoom === 'number') ? mapState.zoom : 14;

  const applyView = (newLat: number, newLng: number, newZoom: number): void => {
    currentZoom = newZoom;
    mapState.centreLat = newLat;
    mapState.centreLng = newLng;
    mapState.zoom = newZoom;
    if (readout) readout.textContent = 'z' + newZoom;
    mapEl.setAttribute('data-lat', String(newLat));
    mapEl.setAttribute('data-lng', String(newLng));
    mapEl.setAttribute('data-zoom', String(newZoom));
    if (loading) loading.classList.remove('hidden');
    if (errorBox) errorBox.classList.add('hidden');
    // Cache-bust the tile so the browser doesn't serve a stale
    // 256x256 from the previous request (the proxy sends
    // Cache-Control: public, max-age=86400).
    tile.src = '/api/staticmap?lat=' + newLat + '&lng=' + newLng + '&zoom=' + newZoom + '&_=' + Date.now();
  };

  tile.addEventListener('load', () => {
    if (loading) loading.classList.add('hidden');
    if (errorBox) errorBox.classList.add('hidden');
  });
  tile.addEventListener('error', () => {
    if (loading) loading.classList.add('hidden');
    if (errorBox) errorBox.classList.remove('hidden');
  });

  // ---- drag to pan ----
  // Pointer events so the same handler covers mouse + touch.
  // The drag is "half a tile" of pixel space per release — we
  // measure the total drag distance and convert to (lat, lng)
  // using tileToLatLng. That keeps the visible experience
  // smooth: even a small drag lands on a fresh tile.
  let dragging = false;
  let startX = 0, startY = 0;
  const onDown = (ev: PointerEvent): void => {
    if (ev.button !== undefined && ev.button !== 0) return;
    dragging = true;
    startX = ev.clientX;
    startY = ev.clientY;
    mapEl.setPointerCapture(ev.pointerId);
    mapEl.style.cursor = 'grabbing';
    ev.preventDefault();
  };
  const onUp = (ev: PointerEvent): void => {
    if (!dragging) return;
    dragging = false;
    try { mapEl.releasePointerCapture(ev.pointerId); } catch { /* released already */ }
    mapEl.style.cursor = 'grab';
    const dx = ev.clientX - startX;
    const dy = ev.clientY - startY;
    if (Math.abs(dx) < 4 && Math.abs(dy) < 4) return; // click, not drag
    const cur = (typeof mapState.centreLat === 'number' && typeof mapState.centreLng === 'number')
      ? { lat: mapState.centreLat, lng: mapState.centreLng }
      : { lat: 0, lng: 0 };
    const next = tileToLatLng(cur.lat, cur.lng, currentZoom, dx, dy);
    applyView(next.lat, next.lng, currentZoom);
  };
  mapEl.addEventListener('pointerdown', onDown);
  mapEl.addEventListener('pointerup', onUp);
  mapEl.addEventListener('pointercancel', onUp);

  // ---- zoom buttons ----
  // + and − change zoom in 1-step increments. Clamped to [1, 18]
  // (the OSM tile pyramid range). On zoom we keep the current
  // centre so the user stays in the same area.
  if (zoomIn) {
    zoomIn.addEventListener('click', (ev) => {
      ev.stopPropagation();
      const z = Math.min(18, currentZoom + 1);
      if (z === currentZoom) return;
      const cur = (typeof mapState.centreLat === 'number' && typeof mapState.centreLng === 'number')
        ? { lat: mapState.centreLat, lng: mapState.centreLng }
        : { lat: 0, lng: 0 };
      applyView(cur.lat, cur.lng, z);
    });
  }
  if (zoomOut) {
    zoomOut.addEventListener('click', (ev) => {
      ev.stopPropagation();
      const z = Math.max(1, currentZoom - 1);
      if (z === currentZoom) return;
      const cur = (typeof mapState.centreLat === 'number' && typeof mapState.centreLng === 'number')
        ? { lat: mapState.centreLat, lng: mapState.centreLng }
        : { lat: 0, lng: 0 };
      applyView(cur.lat, cur.lng, z);
    });
  }
  if (reset) {
    reset.addEventListener('click', (ev) => {
      ev.stopPropagation();
      if (originalLat != null && originalLng != null) {
        applyView(originalLat, originalLng, 14);
      }
    });
  }
}
